import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { Heading } from 'apps/sigma2-ui/src/components/ui/Heading';
import { PayBenchmarkingCompensationFigures } from 'libs/end-to-end/src/pay-benchmarking';
import { PayBenchmarkingCompensationUnit } from 'libs/end-to-end/src/pay-benchmarking';
import { PayCompensationUnitDisplayValues } from 'libs/end-to-end/src/pay-benchmarking';
import { PoolReadinessConfidenceLevel } from 'libs/end-to-end/src/confidence';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { formatPriceAmountToString } from 'libs/end-to-end/src/currencies';
import React from 'react';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

interface PayBenchmarkingCompensationTileViewProps {
  compensationFigures: PayBenchmarkingCompensationFigures;
  compensationUnit: PayBenchmarkingCompensationUnit;
  currency: string;
}

export const PayBenchmarkingCompensationTileView = (
  props: PayBenchmarkingCompensationTileViewProps,
) => {
  const language = useLanguage();

  const {
    marketRangeLowerBound,
    marketRangeUpperBound,
    internalPayBandUpperBound,
    internalPayBandLowerBound,
    suggestedBaseSalary,
    suggestedVariablePay,
    baseSalaryStatisticalConfidence,
    variablePayStatisticalConfidence,
  } = props.compensationFigures;

  const { compensationUnit, currency } = props;

  const displayedSalary = formatPriceAmountToString({
    amount: suggestedBaseSalary,
    currency: currency,
  });

  const displayedVariablePay = formatPriceAmountToString({
    amount: suggestedVariablePay,
    currency: currency,
  });

  const displayedMarketLowerBound = formatPriceAmountToString({
    amount: marketRangeLowerBound,
    currency: currency,
  });

  const displayedMarketUpperBound = formatPriceAmountToString({
    amount: marketRangeUpperBound,
    currency: currency,
  });

  const displayedInternalLowerBound = formatPriceAmountToString({
    amount: internalPayBandLowerBound,
    currency: currency,
  });

  const displayedInternalUpperBound = formatPriceAmountToString({
    amount: internalPayBandUpperBound,
    currency: currency,
  });

  const displayedInternalRange = `${displayedInternalLowerBound} - ${displayedInternalUpperBound}`;
  const displayedMarketRange = `${displayedMarketLowerBound} - ${displayedMarketUpperBound}`;

  const confidenceLevelView = (
    confidenceLevel: PoolReadinessConfidenceLevel,
  ) => {
    switch (confidenceLevel) {
      case 'high':
        return (
          <Paragraph textColor="text-black" className="font-bold">
            {language.statisticalConfidenceHigh()}
          </Paragraph>
        );
      case 'medium':
        return (
          <Paragraph textColor="text-black" className="font-bold">
            {language.statisticalConfidenceMedium()}
          </Paragraph>
        );
      case 'low':
        return (
          <Paragraph textColor="text-black" className="font-bold">
            {language.statisticalConfidenceLow()}
          </Paragraph>
        );
      default:
        throw guardExhaustive(confidenceLevel);
    }
  };

  return (
    <div className="p-2">
      <div className="flex pt-4 items-center justify-between">
        <div>
          <Paragraph textColor="text-black" className="font-bold text-sm">
            Salary
          </Paragraph>
          <div className="flex gap-1 items-end">
            <Heading level={2} textColor={'text-black'}>
              {displayedSalary}
            </Heading>
            <Paragraph textColor="text-gray-600" className="text-sm">
              {PayCompensationUnitDisplayValues[compensationUnit]}
            </Paragraph>
          </div>
          {confidenceLevelView(baseSalaryStatisticalConfidence)}
        </div>

        <div>
          <Paragraph textColor="text-black" className="font-bold text-sm">
            Variable Pay
          </Paragraph>
          <div className="flex gap-1 items-end">
            <Heading level={2} textColor={'text-black'}>
              {displayedVariablePay}
            </Heading>
            <Paragraph textColor="text-gray-600" className="text-sm">
              {PayCompensationUnitDisplayValues[compensationUnit]}
            </Paragraph>
          </div>
          {confidenceLevelView(variablePayStatisticalConfidence)}
        </div>

        <div>
          <div className="flex items-start gap-2">
            <div className="mt-2 rounded bg-blue-800 w-[2rem] h-[0.5rem]" />
            <div>
              <Paragraph textColor="text-black" className="text-sm font-bold">
                Market range:
              </Paragraph>
              <Paragraph textColor="text-black" className="text-sm">
                {displayedMarketRange}
              </Paragraph>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="mt-2 rounded bg-secondary w-[2rem] h-[0.5rem]" />
            <div>
              <Paragraph textColor="text-black" className="text-sm font-bold">
                Internal pay band:
              </Paragraph>
              <Paragraph textColor="text-black" className="text-sm">
                {displayedInternalRange}
              </Paragraph>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
